import { useAssessmentTabs } from "./useAssessmentTabs";
import { useSiteReportConfigurationState } from "./useSiteReportConfiguration";
import { buildLayerPath } from "./useSiteReportConfiguration/pathUtils";
import { AssessmentTab, AssessmentLayer, LayerGroup } from "./types";
import { CheckboxHierarchyState } from "./hooks/useCheckboxHierarchy";

export function tabTranslationKey(tabKey: string) {
  return `site_report.tabs.${tabKey}`;
}

export function layerTranslationKey(layerKey: string) {
  return `site_report.layers.${layerKey}`;
}

/**
 * Calculates the checked state for a group of layers
 *
 * @param layerIds Array of layer IDs to check
 * @param checkedState Current checked state object mapping layer IDs to boolean values
 * @returns boolean | "indeterminate" - The overall checked state for the group
 */
export function getGroupCheckedState(
  layerIds: string[],
  checkedState: Record<string, boolean>
): boolean | "indeterminate" {
  const relevantLayerIds = layerIds.filter((id) => id in checkedState);
  if (relevantLayerIds.length === 0) return false;

  const checkedCount = relevantLayerIds.filter((id) => checkedState[id]).length;

  if (checkedCount === 0) return false;
  if (checkedCount === relevantLayerIds.length) return true;
  return "indeterminate";
}

export type ToggleCheckboxStateParams = {
  tabKey: string;
  layerId?: string;
  groupId?: string;
};

type ToggleCheckboxStateParamsWithState = ToggleCheckboxStateParams & {
  checkboxState: ReturnType<typeof useSiteReportConfigurationState>;
  assessmentTabs: ReturnType<typeof useAssessmentTabs>;
  allPaths: string[];
};

export function toggleCheckboxState({
  tabKey,
  layerId,
  groupId,
  checkboxState,
  assessmentTabs,
  allPaths,
}: ToggleCheckboxStateParamsWithState) {
  if (layerId) {
    const path = buildLayerPath(tabKey, layerId);
    checkboxState.toggleCheckbox(path);
    checkboxState.updateParentState(path, allPaths);
  } else if (groupId) {
    const groupPaths = getGroupLayerPaths(assessmentTabs, tabKey, groupId);
    const allChecked = groupPaths.every((path) => checkboxState.getCheckboxState(path));

    groupPaths.forEach((path) => {
      checkboxState.setCheckboxState(path, !allChecked);
    });

    // Update parent states
    groupPaths.forEach((path) => {
      checkboxState.updateParentState(path, allPaths);
    });
  } else {
    // Toggle entire tab
    const tabPaths = getTabLayerPaths(assessmentTabs, tabKey);
    const allChecked = tabPaths.every((path) => checkboxState.getCheckboxState(path));

    tabPaths.forEach((path) => {
      checkboxState.setCheckboxState(path, !allChecked);
    });

    // Update parent states
    tabPaths.forEach((path) => {
      checkboxState.updateParentState(path, allPaths);
    });
  }
}

// =============================================================================
// LAYER COLLECTION UTILITIES
// =============================================================================

/**
 * Helper function to check if a layer already exists in the active layers list.
 *
 * Prevents duplicate entries by checking all three identifiers that uniquely
 * identify a layer within the hierarchy.
 */
export function isLayerInList(list: AssessmentLayer[], layerId: string, tabKey: string, groupId: string): boolean {
  return list.some((item) => item.layerId === layerId && item.tabKey === tabKey && item.groupId === groupId);
}

/**
 * Utility function to check if two arrays are equal.
 * Used for comparing sectionIds with layerIds arrays.
 */
export function areArraysEqual(arr1: string[], arr2: string[]): boolean {
  return arr1.length === arr2.length && arr1.every((el, i) => el === arr2[i]);
}

/**
 * Gets active layers using simple layer-based logic.
 *
 * This function is used when groups directly contain layers without sections.
 * It checks each layer's checkbox state individually to determine activation.
 */
export function getActiveLayersBasic(
  assessmentTabs: AssessmentTab[],
  checkboxState: CheckboxHierarchyState
): AssessmentLayer[] {
  const layersList: AssessmentLayer[] = [];

  assessmentTabs.forEach((tab) => {
    tab.groups.forEach((group) => {
      group.layerIds.forEach((layerId) => {
        const path = `${tab.key}.${layerId}`;
        if (checkboxState.getCheckboxState(path)) {
          if (!isLayerInList(layersList, layerId, tab.key, group.id)) {
            layersList.push({ layerId, tabKey: tab.key, groupId: group.id });
          }
        }
      });
    });
  });

  return layersList;
}

/**
 * Gets active layers with section-based logic support.
 *
 * This function handles the more complex case where groups can contain sections.
 * It has special logic for:
 * - Groups where sectionIds match layerIds (1:1 mapping)
 * - Groups where any section selection activates all layers
 * - Groups without sections (falls back to basic logic)
 */
export function getActiveLayersWithSections(
  assessmentTabs: AssessmentTab[],
  checkboxState: CheckboxHierarchyState
): AssessmentLayer[] {
  const layersList: AssessmentLayer[] = [];

  assessmentTabs.forEach((tab) => {
    tab.groups.forEach((group) => {
      if (group.sectionIds) {
        // Check if sectionIds and layerIds are the same (1:1 mapping)
        if (areArraysEqual(group.layerIds, group.sectionIds)) {
          group.layerIds.forEach((sectionId) => {
            const path = `${tab.key}.${group.id}.${sectionId}`;
            if (checkboxState.getCheckboxState(path)) {
              if (!isLayerInList(layersList, sectionId, tab.key, group.id)) {
                layersList.push({ layerId: sectionId, tabKey: tab.key, groupId: group.id });
              }
            }
          });
          return;
        }

        // Check if any section is selected, then activate all layers
        const anySectionSelected = group.sectionIds.some((sectionId) => {
          const path = `${tab.key}.${group.id}.${sectionId}`;
          return checkboxState.getCheckboxState(path);
        });

        if (anySectionSelected) {
          group.layerIds.forEach((layerId) => {
            if (!isLayerInList(layersList, layerId, tab.key, group.id)) {
              layersList.push({ layerId, tabKey: tab.key, groupId: group.id });
            }
          });
        }
      } else {
        // Fall back to basic layer-based logic for groups without sections
        group.layerIds.forEach((layerId) => {
          const path = `${tab.key}.${layerId}`;
          const isChecked = checkboxState.getCheckboxState(path);

          if (isChecked) {
            if (!isLayerInList(layersList, layerId, tab.key, group.id)) {
              layersList.push({ layerId, tabKey: tab.key, groupId: group.id });
            }
          }
        });
      }
    });
  });

  return layersList;
}

/**
 * Main function to get active layers with automatic strategy selection.
 *
 * Automatically chooses between basic and section-based logic based on
 * the supportsSectionIds parameter.
 */
export function getActiveLayers(
  assessmentTabs: AssessmentTab[],
  checkboxState: CheckboxHierarchyState,
  supportsSectionIds: boolean
): AssessmentLayer[] {
  if (supportsSectionIds) {
    return getActiveLayersWithSections(assessmentTabs, checkboxState);
  } else {
    return getActiveLayersBasic(assessmentTabs, checkboxState);
  }
}

// =============================================================================
// HIERARCHY TRAVERSAL UTILITIES
// =============================================================================

/**
 * Callback function for layer iteration.
 */
export type LayerIteratorCallback = (
  layerId: string,
  tab: AssessmentTab,
  group: LayerGroup,
  layerIndex: number
) => void;

/**
 * Callback function for group iteration.
 */
export type GroupIteratorCallback = (group: LayerGroup, tab: AssessmentTab, groupIndex: number) => void;

/**
 * Callback function for tab iteration.
 */
export type TabIteratorCallback = (tab: AssessmentTab, tabIndex: number) => void;

/**
 * Iterates through all layers in all groups of all tabs.
 *
 * This replaces the common pattern of nested forEach loops that appears
 * in multiple files throughout the Site Report feature.
 */
export function forEachLayer(assessmentTabs: AssessmentTab[], callback: LayerIteratorCallback): void {
  assessmentTabs.forEach((tab) => {
    tab.groups.forEach((group) => {
      group.layerIds.forEach((layerId, layerIndex) => {
        callback(layerId, tab, group, layerIndex);
      });
    });
  });
}

/**
 * Iterates through all groups in all tabs.
 */
export function forEachGroup(assessmentTabs: AssessmentTab[], callback: GroupIteratorCallback): void {
  assessmentTabs.forEach((tab) => {
    tab.groups.forEach((group, groupIndex) => {
      callback(group, tab, groupIndex);
    });
  });
}

/**
 * Iterates through all tabs.
 */
export function forEachTab(assessmentTabs: AssessmentTab[], callback: TabIteratorCallback): void {
  assessmentTabs.forEach((tab, tabIndex) => {
    callback(tab, tabIndex);
  });
}

/**
 * Collects all layer paths using a callback function.
 *
 * This replaces the pattern of creating an array and pushing to it
 * during traversal.
 */
export function collectLayerPaths(
  assessmentTabs: AssessmentTab[],
  pathBuilder: (layerId: string, tab: AssessmentTab, group: LayerGroup) => string
): string[] {
  const paths: string[] = [];

  forEachLayer(assessmentTabs, (layerId, tab, group) => {
    paths.push(pathBuilder(layerId, tab, group));
  });

  return paths;
}

/**
 * Collects all section paths using a callback function.
 *
 * Handles the section-based traversal pattern where groups can have sections.
 */
export function collectSectionPaths(
  assessmentTabs: AssessmentTab[],
  pathBuilder: (sectionId: string, tab: AssessmentTab, group: LayerGroup) => string
): string[] {
  const paths: string[] = [];

  forEachGroup(assessmentTabs, (group, tab) => {
    if (group.sectionIds) {
      group.sectionIds.forEach((sectionId) => {
        paths.push(pathBuilder(sectionId, tab, group));
      });
    }
  });

  return paths;
}

/**
 * Finds the first layer that matches a predicate.
 */
export function findLayer(
  assessmentTabs: AssessmentTab[],
  predicate: (layerId: string, tab: AssessmentTab, group: LayerGroup) => boolean
): { layerId: string; tab: AssessmentTab; group: LayerGroup } | null {
  for (const tab of assessmentTabs) {
    for (const group of tab.groups) {
      for (const layerId of group.layerIds) {
        if (predicate(layerId, tab, group)) {
          return { layerId, tab, group };
        }
      }
    }
  }
  return null;
}

/**
 * Finds the first group that matches a predicate.
 */
export function findGroup(
  assessmentTabs: AssessmentTab[],
  predicate: (group: LayerGroup, tab: AssessmentTab) => boolean
): { group: LayerGroup; tab: AssessmentTab } | null {
  for (const tab of assessmentTabs) {
    for (const group of tab.groups) {
      if (predicate(group, tab)) {
        return { group, tab };
      }
    }
  }
  return null;
}

/**
 * Finds a tab by its key.
 */
export function findTabByKey(assessmentTabs: AssessmentTab[], tabKey: string): AssessmentTab | null {
  return assessmentTabs.find((tab) => tab.key === tabKey) || null;
}

/**
 * Finds a group by tab key and group ID.
 */
export function findGroupById(
  assessmentTabs: AssessmentTab[],
  tabKey: string,
  groupId: string
): { group: LayerGroup; tab: AssessmentTab } | null {
  const tab = findTabByKey(assessmentTabs, tabKey);
  if (!tab) return null;

  const group = tab.groups.find((g) => g.id === groupId);
  if (!group) return null;

  return { group, tab };
}

/**
 * Filters assessment tabs based on a predicate.
 *
 * This is commonly used for feature flag filtering and other tab filtering needs.
 */
export function filterTabs(
  assessmentTabs: AssessmentTab[],
  predicate: (tab: AssessmentTab) => boolean
): AssessmentTab[] {
  return assessmentTabs.filter(predicate);
}

/**
 * Maps assessment tabs to a new structure while preserving the hierarchy.
 */
export function mapTabs<T>(assessmentTabs: AssessmentTab[], mapper: (tab: AssessmentTab) => T): T[] {
  return assessmentTabs.map(mapper);
}

/**
 * Counts the total number of layers across all tabs and groups.
 */
export function countLayers(assessmentTabs: AssessmentTab[]): number {
  let count = 0;
  forEachLayer(assessmentTabs, () => {
    count++;
  });
  return count;
}

/**
 * Counts the total number of groups across all tabs.
 */
export function countGroups(assessmentTabs: AssessmentTab[]): number {
  let count = 0;
  forEachGroup(assessmentTabs, () => {
    count++;
  });
  return count;
}

/**
 * Gets all layer paths for a specific tab using layer-based format.
 */
export function getTabLayerPaths(assessmentTabs: AssessmentTab[], tabKey: string): string[] {
  const tab = findTabByKey(assessmentTabs, tabKey);
  if (!tab) return [];

  const paths: string[] = [];
  tab.groups.forEach((group) => {
    group.layerIds.forEach((layerId) => {
      paths.push(`${tabKey}.${layerId}`);
    });
  });
  return paths;
}

/**
 * Gets all layer paths for a specific group using layer-based format.
 */
export function getGroupLayerPaths(assessmentTabs: AssessmentTab[], tabKey: string, groupId: string): string[] {
  const result = findGroupById(assessmentTabs, tabKey, groupId);
  if (!result) return [];

  const { group } = result;
  return group.layerIds.map((layerId) => `${tabKey}.${layerId}`);
}

/**
 * Gets all section paths for a specific group using section-based format.
 */
export function getGroupSectionPaths(assessmentTabs: AssessmentTab[], tabKey: string, groupId: string): string[] {
  const result = findGroupById(assessmentTabs, tabKey, groupId);
  if (!result) return [];

  const { group } = result;
  if (!group.sectionIds) return [];

  return group.sectionIds.map((sectionId) => `${tabKey}.${groupId}.${sectionId}`);
}
